/** Inspected source-space ground contacts; never the full alpha box of tall scaffolding.
 * Rear occluded corners are conservative estimates, not proof of a shared 3D camera.
 * Existing single-view details remain single-view: no screen-plane rotation or reflection.
 */
export interface ConstructionContactArt {
  source: string;
  imageSize: readonly [number, number];
  contacts: readonly (readonly [number, number])[];
  basis: string;
}

export const constructionContactArt: Readonly<Record<string, ConstructionContactArt>> = {
  settlement_construction_longhouse_r0_v1: {
    source: 'construction-longhouse-r0-v1.png',
    imageSize: [1536, 1024],
    contacts: [
      [360, 302],
      [1512, 764],
      [1216, 1010],
      [47, 557],
    ],
    basis:
      'elongated front/side lower masonry perimeter; rear corner inferred; about 20px uncertainty',
  },
  settlement_construction_longhouse_r1_v1: {
    source: 'construction-longhouse-r1-v1.png',
    imageSize: [1536, 1024],
    contacts: [
      [985, 295],
      [1340, 470],
      [595, 1005],
      [239, 813],
    ],
    basis:
      'elongated quarter-turn lower masonry perimeter; far corner inferred; about 20px uncertainty',
  },
  settlement_construction_longhouse_r2_v1: {
    source: 'construction-longhouse-r2-v1.png',
    imageSize: [1536, 1024],
    contacts: [
      [365, 294],
      [1516, 771],
      [1200, 1016],
      [49, 552],
    ],
    basis: 'elongated rear lower masonry perimeter; rear corner inferred; about 20px uncertainty',
  },
  settlement_construction_longhouse_r3_v1: {
    source: 'construction-longhouse-r3-v1.png',
    imageSize: [1536, 1024],
    contacts: [
      [992, 320],
      [1379, 480],
      [617, 1017],
      [244, 813],
    ],
    basis:
      'elongated rear quarter-turn lower masonry perimeter; far corner inferred; about 20px uncertainty',
  },
  settlement_survey_ground_v1: {
    source: 'survey-ground-v1.png',
    imageSize: [512, 512],
    contacts: [
      [265, 150],
      [482, 280],
      [253, 434],
      [25, 276],
    ],
    basis: 'ground outside rope corners; excludes upright stake tops; about 8px uncertainty',
  },
  settlement_foundation_pad_v1: {
    source: 'foundation-pad-v1.png',
    imageSize: [512, 512],
    contacts: [
      [260, 239],
      [497, 341],
      [252, 469],
      [12, 340],
    ],
    basis: 'lower masonry footing, rear contact occluded/inferred; about 8px uncertainty',
  },
  settlement_construction_scaffold_v1: {
    source: 'construction-scaffold-v1.png',
    imageSize: [512, 512],
    contacts: [
      [225, 199],
      [500, 340],
      [286, 493],
      [11, 352],
    ],
    basis: 'lower stone footing, rear corner inferred with low confidence; excludes upright beams',
  },
  settlement_planting_beds_v1: {
    source: 'planting-beds-v1.png',
    imageSize: [512, 512],
    contacts: [
      [252, 147],
      [498, 283],
      [268, 451],
      [17, 292],
    ],
    basis: 'lower timber skirt, not raised soil surface; rear inferred, about 8px uncertainty',
  },
  settlement_dock_construction_v1: {
    source: 'dock-construction-v1.png',
    imageSize: [512, 512],
    contacts: [
      [28, 202],
      [48, 180],
      [476, 264],
      [488, 290],
      [410, 455],
      [110, 408],
    ],
    basis: 'conservative irregular rock-bank and pier-foot envelope; not a rectangular planar dock',
  },
  settlement_finishing_scaffold_r1_v1: {
    source: 'finishing-scaffold-r1-v1.png',
    imageSize: [1254, 1254],
    contacts: [
      [600, 846],
      [1058, 1040],
      [744, 1204],
      [650, 1204],
      [212, 1040],
    ],
    basis: 'quarter-turn: four outer stone feet, rear-left ladder occluded; about 12px uncertainty',
  },
  settlement_finishing_scaffold_r2_v1: {
    source: 'finishing-scaffold-r2-v1.png',
    imageSize: [1254, 1254],
    contacts: [
      [592, 856],
      [1052, 1045],
      [736, 1212],
      [624, 1212],
      [224, 1040],
    ],
    basis:
      'rear view: four outer stone feet, rear-right ladder inside envelope; about 12px uncertainty',
  },
  settlement_finishing_scaffold_r3_v1: {
    source: 'finishing-scaffold-r3-v1.png',
    imageSize: [1254, 1254],
    contacts: [
      [720, 868],
      [1012, 1090],
      [889, 1186],
      [751, 1186],
      [508, 1186],
      [256, 1010],
    ],
    basis:
      'three-quarter-turn: four outer stone feet plus near-right ladder feet; about 12px uncertainty',
  },
  settlement_finishing_scaffold_v1: {
    source: 'finishing-scaffold-v1.png',
    imageSize: [1254, 1254],
    contacts: [
      [568, 900],
      [1045, 1040],
      [742, 1206],
      [640, 1206],
      [340, 1127],
      [421, 1165],
      [243, 1040],
    ],
    basis: 'outer stone feet plus ladder feet; excludes tower height; about 12px uncertainty',
  },
};

interface Point {
  x: number;
  y: number;
}

/** Contact shading uses exactly the fitted raster feet, with no screen-space offset. */
export function constructionContactPolygon(
  art: ConstructionContactArt,
  fit: { x: number; y: number; scale: number; originX: number; originY: number },
): Point[] {
  return art.contacts.map(([x, y]) => ({
    x: fit.x + (x - fit.originX * art.imageSize[0]) * fit.scale,
    y: fit.y + (y - fit.originY * art.imageSize[1]) * fit.scale,
  }));
}

/** Largest UNIFORM image scale with every inspected ground contact inside an inset convex plot.
 * Target is projected from effective server bounds, so rectangular rotation happens only once.
 * Matching the ground-envelope center avoids treating an off-center front foot as image center.
 */
export function fitConstructionContact(
  art: ConstructionContactArt,
  target: readonly Point[],
  inset = 0.88,
) {
  if (
    target.length !== 4 ||
    art.contacts.length < 3 ||
    !Number.isFinite(inset) ||
    inset <= 0 ||
    inset > 1 ||
    !art.imageSize.every((value) => Number.isFinite(value) && value > 0) ||
    !target.every(({ x, y }) => Number.isFinite(x) && Number.isFinite(y)) ||
    !art.contacts.every(
      ([x, y]) =>
        Number.isFinite(x) &&
        Number.isFinite(y) &&
        x >= 0 &&
        y >= 0 &&
        x <= art.imageSize[0] &&
        y <= art.imageSize[1],
    )
  )
    return undefined;
  const x = target.reduce((sum, point) => sum + point.x, 0) / 4;
  const y = target.reduce((sum, point) => sum + point.y, 0) / 4;
  const quad = target.map((point) => ({
    x: x + (point.x - x) * inset,
    y: y + (point.y - y) * inset,
  }));
  const left = Math.min(...art.contacts.map(([px]) => px));
  const right = Math.max(...art.contacts.map(([px]) => px));
  const top = Math.min(...art.contacts.map(([, py]) => py));
  const bottom = Math.max(...art.contacts.map(([, py]) => py));
  if (right <= left || bottom <= top) return undefined;
  const cx = (left + right) / 2,
    cy = (top + bottom) / 2;
  const cross = (a: Point, b: Point, p: Point) =>
    (b.x - a.x) * (p.y - a.y) - (b.y - a.y) * (p.x - a.x);
  const sign = Math.sign(cross(quad[0]!, quad[1]!, quad[2]!));
  if (!sign) return undefined;
  let scale = Infinity;
  for (let i = 0; i < 4; i++) {
    const a = quad[i]!,
      b = quad[(i + 1) % 4]!;
    if (sign * cross(a, b, quad[(i + 2) % 4]!) <= 0) return undefined;
    const available = sign * cross(a, b, { x, y });
    for (const [px, py] of art.contacts) {
      const slope = sign * ((b.x - a.x) * (py - cy) - (b.y - a.y) * (px - cx));
      if (slope < 0) scale = Math.min(scale, available / -slope);
    }
  }
  if (!Number.isFinite(scale) || scale <= 0) return undefined;
  return { x, y, scale, originX: cx / art.imageSize[0], originY: cy / art.imageSize[1] };
}
